// 打点相关 (Buried Point / Tracking)
// NOTE: Migrated to modular approach - use @sg/utils/buriedPoint instead
// This file is kept for backwards compatibility
export { SGBuriedPoint, SGBuriedBtnPoint } from './buriedPoint/helper';
// 使用numeral数值格式化库
import Numeral from 'numeral';
/**
 * 数字格式化成BMK
 * data：传入的数字 number或者字符串number
 * num：保留的小数位数，默认0
 * fillZero：位数不够是否填充0，默认false
 * emptyValue: 无数据时如 '' null undefined，需要返回的内容，如 '' '-' 0 等,默认为空 ''
 * */
export const financeFormat = (
  data: string | number,
  num = 0,
  fillZero = false,
  emptyValue: string | number = '',
): string => {
  // Return 'NA' for null/undefined, matching mathFormat pattern
  if (data === null || data === undefined) return 'NA';

  // 获取numeral保留小数位数格式化字符串
  const formatStr = getDecimalNum(num, fillZero);
  // 转BMK对应格式化字符串
  const formatValue = `${formatStr}a`;

  // 筛选空值,空值返回指定内容
  if (isEmptyValue(data)) return String(emptyValue);
  // 非数字值的特殊值,返回原值
  const dataNum = Number(data);
  if (isNaN(dataNum)) return String(data);

  // 获取Numeral对应格式化数据
  const bmkValue = `${Numeral(dataNum).format(formatValue)}`;
  // 获取返回值最后一位字符串
  const lastChar = bmkValue[bmkValue.length - 1];
  // 判断是否是小写字母，是，则转为大写
  const lastCharUpper = /[a-z]/.test(lastChar) ? lastChar?.toUpperCase() : lastChar;
  const BMKValue = bmkValue?.slice(0, -1) + lastCharUpper;
  return BMKValue;
};

// 获取numeral格式保留小数位字符串方法，如 '0.00',并判断是否需要补0
const getDecimalNum = (num: number, fillZero: boolean) => {
  let frontStr = '0.';
  const ZEROSTR = '0';
  const numTemp = Number(num);
  if (numTemp <= 0) return ZEROSTR;
  const numStr = getNumStrings(numTemp, ZEROSTR);

  // 若小数位不够，不需要补0
  if (!fillZero) {
    frontStr = `${frontStr}[${numStr}]`;
  } else {
    frontStr = frontStr + numStr;
  }
  return frontStr;
};

// 根据传入的字符个数和字符，返回对应个数字符连接的字符串
const getNumStrings = (strNum: number, text: string) => {
  let numStr = '';
  let numb = 0;
  while (numb < strNum) {
    numStr = numStr + text;
    numb += 1;
  }
  return numStr;
};

// 统一非数字、空数据处理方法
// 不能用lodash 的isEmpty判断，loadsh的isEmpty会把0判断为空
export const isEmptyValue = (value: string | number) => {
  if (value === null || value === undefined || (typeof value === 'string' && value?.trim() === ''))
    return true;
  return false;
};

// export const mathFormat = (value: string | number, precision?: undefined | number) => {
//   // Handle null/undefined values first
//   if (value === null || value === undefined) return '-';

//   const newVal = Number(value);
//   if (typeof value === 'number' && newVal === 0) return `0M`;
//   if (typeof value === 'string' && value === '') return '-';
//   const formatter = new Intl.NumberFormat('zh-CN');
//   if (newVal >= 10000000000)
//     return Math.round(newVal / 10000000 / 100).toFixed(precision) + 'B';
//   if (newVal >= 1000){
//     const values: any = ((newVal / 10000) / 100).toFixed(precision);
//     return typeof precision === 'number' ? `${formatter.format(values)}M` : `${formatter.format(Math.round(newVal / 10000) / 100)}M`;
//   }
//   if (newVal < -1000){
//     const values: any = ((newVal / 10000) / 100).toFixed(precision);
//     return typeof precision === 'number' ? `${formatter.format(values)}M` : `${formatter.format(Math.round(newVal / 10000) / 100)}M`;
//   }
//   if (newVal >= 1000) {
//     const values: any = ((newVal / 10) / 100).toFixed(precision);
//     return typeof precision === 'number' ? `${formatter.format(values)}K` : `${formatter.format(Math.round(newVal / 10) / 100)}K`;
//   }
//   if (newVal < 1000) {
//     const value: any = Math.round((newVal / 10) / 100).toFixed(precision);
//     return `${formatter.format(Math.round(newVal / 10) / 100)}K`
//   };
//   return formatter.format(Math.round(newVal));
// };

export const mathFormat = (value: string | number, precision: number = 1) => {
  // Handle null/undefined values first
  if (value === null || value === undefined) return 'NA';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '-';
  }

  const newVal = Number(value);
  if (typeof value === 'number' && newVal === 0) return `0.0M`;
  if (typeof value === 'string' && value === '') return '-';
  const millionValue = numericValue / 1_000_000;
  const formatted = millionValue.toFixed(precision);
  return `${formatted}M`;
};

/**
 * Round a raw value to the same precision used in display (millions with 1 decimal place).
 * This ensures that values which appear identical after formatting (e.g. both show "-164.0M")
 * are also numerically equal when used for capsule bar sizing, preventing hidden differences
 * from causing incorrect bar layering.
 */
export const roundToDisplayPrecision = (value?: number | null, decimalPlaces = 1): number => {
  if (value === null || value === undefined) return 0;
  return Number((value / 1_000_000).toFixed(decimalPlaces)) * 1_000_000;
};

export const toTwoDecimalNumber = (value: string | number): string => {
  if (value === null || value === undefined) {
    return '-';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '-';
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      return '-';
    }
    const rounded = Math.round((parsed + Number.EPSILON) * 10) / 10;
    return `${rounded.toFixed(1)}`;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return '-';
    }
    const rounded = Math.round((value + Number.EPSILON) * 10) / 10;
    return `${rounded.toFixed(1)}`;
  }

  return '-';
};
/**
 * Converts a number to millions with controlled decimal places.
 * Used for table display where headers already indicate "(M$)".
 * @param {number | string | null | undefined} value - Value to convert
 * @param {number} [decimalPlaces=1] - Number of decimal places (default: 1)
 * @returns {string} Converted value with fixed decimal places or '-' for null/undefined
 * @example toMillions(816000000); // "816.0"
 * @example toMillions(5123456); // "5.1"
 * @example toMillions(-29000000); // "-29.0"
 * @example toMillions(null); // '-'
 */
export const toMillions = (value: number | string | null | undefined, decimalPlaces: number = 1): string => {
  if (value === null || value === undefined) return 'NA';
  const numValue = Number(value);
  if (!Number.isFinite(numValue)) return '-';
  // if (isNaN(numValue)) return '-';
  // return Math.round((numValue / 1000000) * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
  const millionValue = numValue / 1_000_000;
  return millionValue.toFixed(decimalPlaces);
};

export function getChartBufferWidth(defaultWidth: number = 0) {
  let language = getChromeLanguage();
  if (language === 'zh-CN') {
    return defaultWidth;
  }
  return 10;
}

export function getChromeLanguage() {
  const nav: any = navigator;
  return navigator.language || nav?.userLanguage;
}

/**
 * Formats a decimal value as a percentage string.
 * Converts decimal to percentage and formats with 2 decimal places.
 * @param {string} value - Decimal value as string (e.g., "0.1234" or "-0.05")
 * @returns {string} Formatted percentage string (e.g., "12.34%" or "-5.00%")
 * @example formatPercentage("0.1234"); // "12.34%"
 * @example formatPercentage("-0.05"); // "-5.00%"
 * @example formatPercentage(""); // "-"
 */
export function formatPercentage(value: string | null | undefined): string {
  if (!value || value === null || value === undefined) return '-';

  const num = parseFloat(value);
  if (isNaN(num)) return '-';

  return `${num >= 0 ? '' : '-'}${Math.abs(num * 100).toFixed(2)}%`;
}

/**
 * Formats a number as a percentage string by adding the % symbol (no conversion).
 * The value is already in percentage format, this function just adds the unit.
 * @param {number | null | undefined} value - Number value already in percentage format (e.g., 12.4 for 12.4%)
 * @param {number} [precision=1] - Number of decimal places to display (default: 1)
 * @returns {string} Formatted percentage string (e.g., "12.4%" or "NA")
 * @example formatPercentValue(12.4); // "12.4%"
 * @example formatPercentValue(12.456, 2); // "12.46%"
 * @example formatPercentValue(null); // "NA"
 */
export function formatPercentValue(value: number | null | undefined, precision: number = 1): string {
  if (value === null || value === undefined) return 'NA';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'NA';
  }
  return `${numericValue.toFixed(precision)}%`;
}

export function formatPercentValue100(value: number | null | undefined, precision: number = 1): string {
  if (value === null || value === undefined) return 'NA';
  const numericValue = Number(value * 100);
  if (!Number.isFinite(numericValue)) {
    return 'NA';
  }
  return `${numericValue.toFixed(precision)}%`;
}

export function formatVSValue(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return 'NA';
  }
  const dValue = Number(value.toFixed(1));
  const valueCur = toMillions(dValue);

  if (Number.isNaN(dValue)) {
    return 'NA';
  }
  if (dValue > 0) {
    return `+${valueCur}`;
  }



  return `${valueCur}`;
}

/**
 * Determines the display format for a dashboard metric based on its name.
 * Returns 'currency' for revenue/payment metrics, 'percent' for rates/retention metrics, 'hour' for time metrics, or 'normal' otherwise.
 * @param {string} metricName - Name of the metric (e.g., 'revenue', 'payer_rate', 'd1t1', 'median_online_time')
 * @returns {'normal' | 'currency' | 'percent' | 'hour'} Format type for the metric
 * @example getDashboardMetricFormat('revenue'); // "currency"
 * @example getDashboardMetricFormat('payer_rate'); // "percent"
 * @example getDashboardMetricFormat('median_online_time'); // "hour"
 * @example getDashboardMetricFormat('active_users'); // "normal"
 */
export function getDashboardMetricFormat(metricName: string): 'normal' | 'currency' | 'percent' | 'hour' {
  if (
    metricName.includes('revenue') ||
    metricName.includes('pay_amount') ||
    metricName.includes('arpu') ||
    metricName === 'revenue_after_chargeback' ||
    metricName === 'lifetime_revenue_after_refund'
  ) {
    return 'currency';
  }
  if (metricName.includes('rate') || metricName.includes('retention') || metricName.includes('d2t1') || metricName.includes('d1t1') || metricName.includes('d7t7') || metricName.includes('d2t2')) {
    return 'percent';
  }
  if (metricName === 'median_online_time' || metricName === 'aot_hour') {
    return 'hour';
  }
  return 'normal';
}

/**
 * Formats a number as normal, currency, percent, or hour.
 * @param {number} num - Number to format.
 * @param {'normal'|'currency'|'percent'|'hour'} [format='normal'] - Display style.
 * @returns {string} Formatted number string.
 * @example
 * formatDashboardMetric(1234, 'normal'); // "1,234"
 * @example formatDashboardMetric(1234, 'currency'); // "$1,234.00"
 * @example formatDashboardMetric(1500000, 'currency'); // "$1.50M"
 * @example formatDashboardMetric(0.5, 'percent'); // "50.00%"
 * @example formatDashboardMetric(2.5, 'hour'); // "2.50H"
 * @example formatDashboardMetric(0, 'hour'); // "0.00H"
 */
export function formatDashboardMetric(num: number, format: 'normal' | 'currency' | 'percent' | 'hour' = 'normal') {
  if (format === 'currency') {
    if (num >= 1000000) {
      const millionValue = num / 1000000;
      return '$' + millionValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + 'M';
    }
    return '$' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (format === 'percent') {
    const percentValue = num * 100;
    return percentValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  }
  if (format === 'hour') {
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + 'H';
  }
  // normal
  return num.toLocaleString();
}

/**
* Determines the current and previous quarters.
* @returns {object} An object with current and previous quarter keys, e.g., { current: 'Q3', previous: 'Q2' }
*/
export const getCurrentQuarters = () => {
  const month = new Date().getMonth();
  const currentQuarter = Math.floor(month / 3) + 1;
  const previousQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1;
  return {
    current: `Q${currentQuarter}`,
    previous: `Q${previousQuarter}`,
  };
};

import { indicatorValRankingFormat } from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overview/PerformanceSummary/cardUtils';

export const getNumConfig = (rate: string, index: number) => {
  const rateArr = rate?.split(',') || [];
  const rawSegment = (rateArr[index] ?? '').trim().replace(/%$/, '');
  if (rawSegment === '' || rawSegment === '-') {
    return undefined;
  }

  const formattedText = `${rawSegment}%`;
  const numericValue = Number(rawSegment);

  // indicatorValRankingFormat only matches exact '0%' / '0.00', not '0.00%'.
  if (!Number.isNaN(numericValue) && numericValue === 0) {
    return {
      numContent: formattedText,
      numColor: 'grayColor',
      numSize: 'zero',
      arrow: false,
      arrowColor: '',
      isShowZero: true,
    };
  }

  const {
    _indicatorContent,
    _indicatorSize,
    _indicatorColor,
    _isIndicatorArrow,
    _indicatprsArrowColor,
  } = indicatorValRankingFormat(formattedText);

  return {
    numContent: _indicatorContent,
    numColor: _indicatorColor,
    numSize: _indicatorSize,
    arrow: _isIndicatorArrow,
    arrowColor: _indicatprsArrowColor,
    isShowZero: true,
  };
};

/**
 * Formats a compact date string (紧凑日期) into a readable format.
 * @param {string} dateStr - Date string in 'yyyymmdd' or 'yyyymm'.
 * @param {number} dateConfirm - Date confirmation level (2 = year-month only, otherwise full date).
 * @returns {string} Formatted date string.
 * @example formatCompactDate("20250101", 1); // "2025-01-01"
 * @example formatCompactDate("20250101", 2); // "2025-01"
 */
export const formatCompactDateString = (dateStr: string, dateConfirm?: number) => {
  if (!dateStr) return dateStr;

  // If date_confirm is 2, show only year and month (YYYY-MM)
  if (dateConfirm === 2) {
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}`;
  }

  // Otherwise, show full date (YYYY-MM-DD)
  if (dateStr.length >= 8) {
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
  }

  // Fallback for 6-character dates (yyyymm)
  if (dateStr.length === 6) {
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}`;
  }

  return dateStr;
};

/**
 * Parses a compact date string (`yyyymmdd` or `yyyymm`) into a JavaScript Date object.
 * - If only year and month are provided (`yyyymm`), defaults day to 1.
 *
 * @param {string} dateStr - Date string in `yyyymmdd` or `yyyymm` format.
 * @returns {Date} A JavaScript Date object representing the parsed date.
 */
export const parseCompactDate = (dateStr: string) => {
  // parseInt(x, 10), base 10 (this is because old JS might use base 8 by default)
  const year = parseInt(dateStr.slice(0, 4), 10);
  // Month is 0-indexed (0 = January, ..., 11 = December)
  const month = parseInt(dateStr.slice(4, 6), 10) - 1;
  // if day is not defined, default to 1st of month
  const day = dateStr.length === 8 ? parseInt(dateStr.slice(6, 8), 10) : 1;
  return new Date(year, month, day);
};

/**
 * Builds an update time string from end date
 * @param end - End date in format "YYYY-MM" (e.g., "2025-10")
 * @returns Update time string in format "Update on YYYY-MM" (e.g., "Update on 2025-10")
 */
export const buildUpdateTime = (end?: string): string => {
  if (!end) {
    return '';
  }
  return `Update on ${end}`;
};

/**
 * Builds a period string from start to end date
 * @param end - End date in format "YYYY-MM" (e.g., "2025-10")
 * @param start - Optional start date in format "YYYY-MM" (e.g., "2020-01")
 *                If provided, uses start directly; otherwise extracts year from end and uses YYYY-01
 * @returns Period string in format "YYYY-MM~YYYY-MM" (e.g., "2025-01~2025-10");
 *          when only start is provided, returns "YYYY-MM~NA"
 */
export const buildPeriod = (end?: string | null, start?: string | null): string => {
  const hasEnd = !!end;
  const hasStart = !!start;

  if (hasEnd) {
    // If start is provided, use it directly; otherwise extract year from end date
    const startDate = hasStart ? start : `${end.split('-')[0]}-01`;
    return `${startDate}~${end}`;
  }

  // Backend may return start without end (e.g. approved_dev_cost_end_date: null)
  if (hasStart) {
    return `${start}~NA`;
  }

  return '';
};

/**
 * Builds a full year period string from start to end of year
 * @param end - End date in format "YYYY-MM" (e.g., "2025-10")
 * @returns Period string in format "YYYY-01~YYYY-12" (e.g., "2025-01~2025-12")
 */
export const buildFullYearPeriod = (end?: string): string => {
  if (!end) {
    return '';
  }
  // Extract year from end date (e.g., "2025-10" -> "2025")
  const year = end.split('-')[0];
  return `${year}-01~${year}-12`;
};

/**
 * Helper function to extract year prefix from datatime
 * @param datatime - Date string in format "YYYY-MM" (e.g., "2026-02", "2025-01")
 * @returns Two-digit year prefix (e.g., "26" for "2026-02", "25" for "2025-01")
 */
export const getYearPrefix = (datatime?: string): string => {
  if (!datatime) return '';
  const yearMatch = datatime.match(/^(\d{4})-/);
  return yearMatch ? yearMatch[1].slice(-2) : '';
};

/**
 * Get month-year prefix from datatime string for "By Month" table headers.
 * @param datatime - Date string in "YYYY-MM" format
 * @returns Year + month prefix (e.g., "202510" for "2025-10")
 */
export const getMonthYearPrefix = (datatime?: string): string => {
  if (!datatime) return '';
  const match = datatime.match(/^(\d{4})-(\d{2})/);
  return match ? `${match[1]}${match[2]}` : '';
};

/**
 * Compare labels with digits-first (0-9), then A-Z natural order.
 * Used by Board Governance studio filter and MKTCost project options.
 */
export const compareAlphaNumericText = (aText = '', bText = ''): number => {
  const aStartsWithNumber = /^\d/.test(aText);
  const bStartsWithNumber = /^\d/.test(bText);

  // Numbers before letters so "0-xxx" / "1-xxx" sit above A-Z names.
  if (aStartsWithNumber && !bStartsWithNumber) return -1;
  if (!aStartsWithNumber && bStartsWithNumber) return 1;

  return aText.localeCompare(bText, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

/** Stable copy + sort by a text getter using {@link compareAlphaNumericText}. */
export const sortByAlphaNumericText = <T>(
  items: T[],
  getText: (item: T) => string,
): T[] => [...items].sort((a, b) => compareAlphaNumericText(getText(a), getText(b)));

